import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SortIcon from '../assets/icons/sort.svg';
import ConversationPanel from '../components/messages/ConversationPanel';
import FilterDropdown from '../components/filtering/FilterDropdown';
import Loading from '../components/Loading';
import { useMessageActions } from '../hooks/useMessageActions';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { usePatientMetadata } from '../providers/PatientMetadataProvider';
import { usePatientList } from '../providers/patient/PatientsListProvider';
import { useToast } from '../providers/ToastProvider';
import { timeAgo, messagePreviewText } from '@odo/shared-ui';

const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 40;
const MIN_PAGE_SIZE = 5;
const MAX_PAGE_SIZE = 50;
const VIEW_MODE_KEY = 'messagesTableViewMode';

interface ConversationRow {
    patientId: string;
    name: string;
    profilePicUrl: string | undefined;
    treatmentStatus: string | null;
    startDate: string | null;
    alignerNumber: number | null;
    alignerTotal: number | null;
    requiredAction: string | null;
    lastMessageText: string | null;
    lastMessageDate: string | null;
}

type ViewFilter = 'unread' | 'all' | 'seen';

const VIEW_FILTERS: { label: string; value: ViewFilter }[] = [
    { label: 'Unread', value: 'unread' },
    { label: 'All', value: 'all' },
    { label: 'Treated', value: 'seen' }
];

const Messages: React.FC = () => {
    const {
        unreadPatientIds,
        allConversationIds,
        getProfilePictureUrl,
        getLastMessage,
        isLoading: metadataLoading,
        isFetching: metadataFetching,
        refresh: refreshMetadata
    } = usePatientMetadata();
    const { patients, isLoading: patientsLoading, isFetching: patientsFetching } = usePatientList();
    const { showToast } = useToast();
    const [viewFilter, setViewFilter] = useState<ViewFilter>(
        () => (sessionStorage.getItem('conv_viewFilter') as ViewFilter) ?? 'unread'
    );
    const [viewFilterOpen, setViewFilterOpen] = useState(false);
    const viewFilterRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (viewFilterRef.current && !viewFilterRef.current.contains(event.target as Node)) {
                setViewFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPatient, setSelectedPatient] = useState<ConversationRow | null>(null);
    const [sortType, setSortType] = useState<'name' | 'date'>('date');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [statusFilter, setStatusFilter] = useState<string[]>(() => {
        try {
            return JSON.parse(sessionStorage.getItem('conv_statusFilter') ?? '[]');
        } catch {
            return [];
        }
    });
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState<'paged' | 'scroll'>(() => {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        return stored === 'paged' ? 'paged' : 'scroll';
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const [availableHeight, setAvailableHeight] = useState<number | undefined>(undefined);
    const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);

    // C2 fix: inline computePageSize into effects to avoid stale closure
    useLayoutEffect(() => {
        function handleResize() {
            if (containerRef.current) {
                const top = containerRef.current.getBoundingClientRect().top;
                setAvailableHeight(window.innerHeight - top);
            }
            if (scrollAreaRef.current) {
                const usableHeight = scrollAreaRef.current.clientHeight - HEADER_HEIGHT;
                const computed = Math.floor(usableHeight / ROW_HEIGHT);
                setPageSize(Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, computed)));
            }
        }
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [patientsLoading]); // M4 fix: removed metadataLoading (not used in effect body)

    useLayoutEffect(() => {
        if (scrollAreaRef.current) {
            const usableHeight = scrollAreaRef.current.clientHeight - HEADER_HEIGHT;
            const computed = Math.floor(usableHeight / ROW_HEIGHT);
            setPageSize(Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, computed)));
        }
    }, [availableHeight]);

    // H2 fix: read current values and set both independently (no nested updaters)
    const handleSortClick = (type: 'name' | 'date') => {
        if (type === sortType) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortType(type);
            setSortDir(type === 'date' ? 'desc' : 'asc');
        }
        setPage(1);
    };

    const handleViewModeChange = (mode: 'paged' | 'scroll') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_MODE_KEY, mode);
        if (mode === 'paged') {
            setPage(1);
        }
    };

    const sourceIds = useMemo(() => {
        if (viewFilter === 'unread') return unreadPatientIds;
        if (viewFilter === 'seen') {
            const seen = new Set<string>();
            for (const id of allConversationIds) {
                if (!unreadPatientIds.has(id)) seen.add(id);
            }
            return seen;
        }
        return allConversationIds;
    }, [viewFilter, unreadPatientIds, allConversationIds]);

    const conversations: ConversationRow[] = useMemo(() => {
        return Array.from(sourceIds).map((patientId) => {
            const patient = patients.find((p) => p.id === patientId);
            const lastMessage = getLastMessage(patientId);

            return {
                patientId,
                name: patient?.name ?? 'Unknown Patient',
                profilePicUrl: getProfilePictureUrl(patientId),
                treatmentStatus: patient?.treatmentStatus ?? null,
                startDate: patient?.startDate ?? null,
                alignerNumber: patient?.alignerNumber ?? null,
                alignerTotal: patient?.alignerTotal ?? null,
                requiredAction: patient?.requiredAction ?? null,
                lastMessageText: lastMessage?.text ?? null,
                lastMessageDate: lastMessage?.date ?? null
            };
        });
    }, [sourceIds, patients, getProfilePictureUrl, getLastMessage]);

    const statusOptions = useMemo(() => {
        const statuses = new Set<string>();
        conversations.forEach((c) => {
            if (c.treatmentStatus) statuses.add(c.treatmentStatus);
        });
        return Array.from(statuses).sort();
    }, [conversations]);

    const filteredConversations = useMemo(() => {
        let filtered = conversations;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter((c) => c.name.toLowerCase().includes(query));
        }

        if (statusFilter.length > 0) {
            filtered = filtered.filter(
                (c) => c.treatmentStatus && statusFilter.includes(c.treatmentStatus)
            );
        }

        return filtered;
    }, [conversations, searchQuery, statusFilter]);

    const sortedConversations = useMemo(() => {
        const sorted = [...filteredConversations];
        sorted.sort((a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            switch (sortType) {
                case 'name': {
                    const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                    return cmp * dir;
                }
                case 'date': {
                    const dateA = a.lastMessageDate ? new Date(a.lastMessageDate).getTime() : 0;
                    const dateB = b.lastMessageDate ? new Date(b.lastMessageDate).getTime() : 0;
                    const cmp = dateA - dateB;
                    if (cmp !== 0) return cmp * dir;
                    return a.name.localeCompare(b.name);
                }
                default:
                    return 0;
            }
        });
        return sorted;
    }, [filteredConversations, sortType, sortDir]);

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * pageSize;
        return sortedConversations.slice(start, start + pageSize);
    }, [sortedConversations, page, pageSize]);

    const displayedItems = viewMode === 'scroll' ? sortedConversations : paginatedItems;
    const totalPages = Math.max(1, Math.ceil(sortedConversations.length / pageSize));

    useEffect(() => {
        setPage(1);
    }, [searchQuery, statusFilter, pageSize]);

    // Persist filters to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('conv_statusFilter', JSON.stringify(statusFilter));
    }, [statusFilter]);
    useEffect(() => {
        sessionStorage.setItem('conv_viewFilter', viewFilter);
    }, [viewFilter]);

    // Sync selectedPatient → URL
    useEffect(() => {
        const currentParam = searchParams.get('patientId');
        const selectedId = selectedPatient?.patientId ?? null;
        if (selectedId && currentParam !== selectedId) {
            const next = new URLSearchParams(searchParams);
            next.set('patientId', selectedId);
            setSearchParams(next, { replace: true });
        } else if (!selectedId && currentParam) {
            const next = new URLSearchParams(searchParams);
            next.delete('patientId');
            setSearchParams(next, { replace: true });
        }
    }, [selectedPatient, searchParams, setSearchParams]);

    // On mount: restore panel from URL param
    const hasRestoredRef = useRef(false);
    useEffect(() => {
        if (hasRestoredRef.current) return;
        const paramId = searchParams.get('patientId');
        if (!paramId || conversations.length === 0) return;
        const match = conversations.find((c) => c.patientId === paramId);
        if (match) {
            setSelectedPatient(match);
            hasRestoredRef.current = true;
        }
    }, [searchParams, conversations]);

    const handleClosePanel = useCallback(() => {
        setSelectedPatient(null);
    }, []);

    const { markingSeenId, markingUnreadId, handleMarkAsSeen, handleMarkAsUnread } =
        useMessageActions(refreshMetadata, showToast);

    const {
        selectMode,
        setSelectMode,
        selectedIds,
        setSelectedIds,
        isBulkProcessing,
        exitSelectMode,
        toggleSelect,
        toggleSelectAll,
        selectionType,
        handleBulkMarkAsDone,
        handleBulkMarkAsUnread
    } = useMultiSelect(displayedItems, unreadPatientIds, refreshMetadata, showToast, [
        viewFilter,
        searchQuery,
        statusFilter,
        page,
        sortType,
        sortDir
    ]);

    const isLoading = patientsLoading || metadataLoading;
    const isFetching = patientsFetching || metadataFetching;
    const hasActiveFilters = searchQuery.trim().length > 0 || statusFilter.length > 0;

    if (isLoading) return <Loading />;

    return (
        <div className="flex flex-col h-full w-full">
            {/* Header */}
            <div className="px-8 pt-8">
                <h2 className="text-2xl font-bold text-gray-800">Messages</h2>
            </div>

            {/* Action bar */}
            <div className="flex items-center justify-between w-full px-8 mt-5">
                <div className="flex items-center gap-4">
                    <div className="flex items-center w-[340px] h-11 bg-white border border-gray-200 rounded-xl px-4">
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                        <input
                            type="text"
                            placeholder="Search by patient name"
                            className="w-full bg-transparent outline-none text-gray-700 placeholder-gray-400 text-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="ml-2 text-gray-400 hover:text-gray-600"
                                aria-label="Clear search"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                        className={`inline-flex items-center gap-2 rounded-full border-2 shadow-sm px-4 py-2 text-sm font-medium transition-all duration-300 focus:outline-none ${
                            selectMode
                                ? 'bg-gray-800 text-white border-gray-800'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {selectMode && <XMarkIcon className="w-4 h-4" />}
                        {selectMode ? 'Cancel' : 'Select'}
                    </button>
                    {/* View filter dropdown */}
                    <div className="relative inline-block text-left" ref={viewFilterRef}>
                        <button
                            type="button"
                            onClick={() => setViewFilterOpen((prev) => !prev)}
                            className={`inline-flex justify-between items-center rounded-full border-2 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300 ${
                                viewFilterOpen ? 'border-gray-700' : 'border-gray-300'
                            }`}
                            style={{ minWidth: '140px' }}
                        >
                            <span>{VIEW_FILTERS.find((f) => f.value === viewFilter)?.label}</span>
                            <ChevronDownIcon
                                className={`h-5 w-5 ml-2 transition-transform duration-300 ${viewFilterOpen ? 'rotate-180' : ''}`}
                            />
                        </button>
                        <div
                            className={`absolute right-0 mt-2 w-40 z-20 bg-white shadow-lg rounded-2xl transition-all duration-300 ease-in-out overflow-hidden ring-2 ${
                                viewFilterOpen ? 'ring-gray-700' : 'ring-transparent'
                            }`}
                            style={{
                                maxHeight: viewFilterOpen ? '12rem' : '0',
                                opacity: viewFilterOpen ? 1 : 0
                            }}
                        >
                            <div className="py-1">
                                {VIEW_FILTERS.map((f) => (
                                    <button
                                        key={f.value}
                                        type="button"
                                        onClick={() => {
                                            setViewFilter(f.value);
                                            setPage(1);
                                            setViewFilterOpen(false);
                                        }}
                                        className={`flex items-center w-full px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                                            viewFilter === f.value
                                                ? 'bg-gray-100 text-gray-900 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span
                                            className={`w-2 h-2 rounded-full mr-3 ${
                                                viewFilter === f.value
                                                    ? 'bg-gray-800'
                                                    : 'bg-transparent'
                                            }`}
                                        />
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <FilterDropdown
                        title="Status"
                        options={statusOptions}
                        selectedOptions={statusFilter}
                        onChange={setStatusFilter}
                        align="right"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 min-h-0 flex flex-col w-full">
                <div
                    className="px-8 py-4 w-full flex flex-col overflow-hidden"
                    ref={containerRef}
                    style={{ height: availableHeight }}
                >
                    <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.15)] w-full flex-1 min-h-0 flex flex-col">
                        {/* Bulk actions bar */}
                        {selectMode && (
                            <div className="px-6 py-2.5 border-b border-gray-200 bg-gray-50/80 flex items-center gap-3">
                                {selectedIds.size === 0 ? (
                                    <span className="text-sm text-gray-500">
                                        Select conversations to perform bulk actions
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-sm font-medium text-gray-700">
                                            {selectedIds.size} selected
                                        </span>
                                        <div className="w-px h-4 bg-gray-300" />
                                        {selectionType === 'mixed' ? (
                                            <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                                Select only unread or only treated conversations
                                            </span>
                                        ) : selectionType === 'unread' ? (
                                            <button
                                                onClick={handleBulkMarkAsDone}
                                                disabled={isBulkProcessing}
                                                className="flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 hover:bg-green-100 transition-colors disabled:opacity-50"
                                            >
                                                {isBulkProcessing ? (
                                                    <div className="h-3.5 w-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    'Mark all as done'
                                                )}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleBulkMarkAsUnread}
                                                disabled={isBulkProcessing}
                                                className="flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 hover:bg-orange-100 transition-colors disabled:opacity-50"
                                            >
                                                {isBulkProcessing ? (
                                                    <div className="h-3.5 w-3.5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    'Mark all as unread'
                                                )}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Background sync indicator */}
                        {isFetching && !isLoading && (
                            <div className="px-6 py-2.5 border-b border-gray-200 bg-gradient-to-r from-blue-50/50 to-white flex items-center gap-2.5">
                                <div className="animate-spin h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent rounded-full" />
                                <p className="text-sm text-gray-600 font-medium">
                                    Syncing latest data...
                                </p>
                            </div>
                        )}

                        {/* Filter results info */}
                        {hasActiveFilters && (
                            <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                                <p className="text-sm text-gray-600">
                                    {sortedConversations.length === 0
                                        ? `No conversations found${searchQuery ? ` matching "${searchQuery}"` : ''}`
                                        : `Found ${sortedConversations.length} conversation${sortedConversations.length === 1 ? '' : 's'}${searchQuery ? ` matching "${searchQuery}"` : ''}`}
                                </p>
                            </div>
                        )}

                        <div
                            ref={scrollAreaRef}
                            className="flex-1 min-h-0 overflow-y-auto scrollbar-hide"
                        >
                            <table className="min-w-full">
                                <thead className="sticky top-0 z-10 bg-white">
                                    <tr className="h-10 shadow-[0px_1px_2px_rgba(0,0,0,0.15)] border-b border-gray-200">
                                        {selectMode && (
                                            <th className="text-header rounded-tl-xl w-12 !px-0">
                                                <div className="flex items-center justify-center">
                                                    <button
                                                        onClick={toggleSelectAll}
                                                        className={`group w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center transition-all duration-200 ${
                                                            displayedItems.length > 0 &&
                                                            displayedItems.every((r) =>
                                                                selectedIds.has(r.patientId)
                                                            )
                                                                ? 'bg-gray-800 border-gray-800'
                                                                : displayedItems.some((r) =>
                                                                        selectedIds.has(r.patientId)
                                                                    )
                                                                  ? 'bg-gray-300 border-gray-400'
                                                                  : 'border-gray-300 hover:border-gray-500 bg-white'
                                                        }`}
                                                    >
                                                        {displayedItems.length > 0 &&
                                                        displayedItems.every((r) =>
                                                            selectedIds.has(r.patientId)
                                                        ) ? (
                                                            <svg
                                                                className="w-2.5 h-2.5 text-white"
                                                                viewBox="0 0 10 8"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    d="M1 4L3.5 6.5L9 1"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        ) : displayedItems.some((r) =>
                                                              selectedIds.has(r.patientId)
                                                          ) ? (
                                                            <svg
                                                                className="w-2.5 h-2.5 text-white"
                                                                viewBox="0 0 10 2"
                                                                fill="none"
                                                            >
                                                                <path
                                                                    d="M1 1H9"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        ) : null}
                                                    </button>
                                                </div>
                                            </th>
                                        )}
                                        <th
                                            className={`text-header !pr-0 ${!selectMode ? 'rounded-tl-xl' : ''}`}
                                        >
                                            <button
                                                type="button"
                                                className="flex items-center gap-1 select-none"
                                                onClick={() => handleSortClick('name')}
                                            >
                                                PATIENT{' '}
                                                <img
                                                    src={SortIcon}
                                                    alt="Sort"
                                                    className="w-4 h-4"
                                                />
                                            </button>
                                        </th>
                                        <th className="text-header !pl-2 !pr-2">STATUS</th>
                                        <th className="text-header">LAST MESSAGE</th>
                                        <th className="text-header">
                                            <button
                                                type="button"
                                                className="flex items-center gap-1 select-none"
                                                onClick={() => handleSortClick('date')}
                                            >
                                                DATE{' '}
                                                <img
                                                    src={SortIcon}
                                                    alt="Sort"
                                                    className="w-4 h-4"
                                                />
                                            </button>
                                        </th>
                                        <th className="text-header rounded-tr-xl w-20" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={selectMode ? 6 : 5} className="px-6 py-16">
                                                {hasActiveFilters ? (
                                                    <p className="text-sm text-gray-500 text-center">
                                                        No conversations found matching your
                                                        filters.
                                                    </p>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center gap-4">
                                                        <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center">
                                                            <svg
                                                                className="w-8 h-8 text-yellow-500"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                                strokeWidth={2}
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <p className="text-lg font-semibold text-gray-800">
                                                            You're all caught up!
                                                        </p>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        displayedItems.map((row) => (
                                            <tr
                                                key={row.patientId}
                                                onClick={() =>
                                                    selectMode
                                                        ? setSelectedIds((prev) => {
                                                              const next = new Set(prev);
                                                              if (next.has(row.patientId))
                                                                  next.delete(row.patientId);
                                                              else next.add(row.patientId);
                                                              return next;
                                                          })
                                                        : setSelectedPatient(row)
                                                }
                                                className={`h-16 transition-colors duration-150 border-b border-gray-200 text-sm cursor-pointer ${
                                                    selectMode && selectedIds.has(row.patientId)
                                                        ? 'bg-gray-100/80 ring-1 ring-inset ring-gray-300'
                                                        : unreadPatientIds.has(row.patientId)
                                                          ? 'text-gray-900 bg-yellow-50/40 font-medium hover:bg-yellow-50/70 border-l-2 border-l-yellow-400'
                                                          : 'text-gray-400 bg-gray-50/50 hover:bg-gray-50'
                                                }`}
                                            >
                                                {/* Checkbox */}
                                                {selectMode && (
                                                    <td
                                                        className="py-0 align-middle w-12 !px-0"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="flex items-center justify-center">
                                                            <button
                                                                onClick={(e) =>
                                                                    toggleSelect(e, row.patientId)
                                                                }
                                                                className={`w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center transition-all duration-200 ${
                                                                    selectedIds.has(row.patientId)
                                                                        ? 'bg-gray-800 border-gray-800 scale-110'
                                                                        : 'border-gray-300 hover:border-gray-500 bg-white hover:shadow-sm'
                                                                }`}
                                                            >
                                                                {selectedIds.has(row.patientId) && (
                                                                    <svg
                                                                        className="w-2.5 h-2.5 text-white"
                                                                        viewBox="0 0 10 8"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M1 4L3.5 6.5L9 1"
                                                                            stroke="currentColor"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}

                                                {/* Patient: dot + avatar + name */}
                                                <td className="pl-6 pr-0 py-0 align-middle whitespace-nowrap">
                                                    <div className="flex items-center gap-4">
                                                        <div
                                                            className={`w-2 h-2 rounded-full flex-shrink-0 ${unreadPatientIds.has(row.patientId) ? 'bg-yellow-400' : 'bg-transparent'}`}
                                                        />
                                                        <div className="w-8 h-8 rounded-full ring-2 ring-gray-200 overflow-hidden bg-yellow-400 flex items-center justify-center flex-shrink-0">
                                                            {row.profilePicUrl ? (
                                                                <img
                                                                    src={row.profilePicUrl}
                                                                    alt={row.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <span className="text-xs font-bold text-gray-600">
                                                                    {row.name
                                                                        ?.charAt(0)
                                                                        ?.toUpperCase() || '?'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-normal leading-4 tracking-[0.005em] text-gray-800 underline cursor-pointer truncate max-w-[140px]">
                                                            {row.name}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Treatment status */}
                                                <td className="pl-2 pr-2 py-0 align-middle whitespace-nowrap">
                                                    <span className="truncate block">
                                                        {row.treatmentStatus || '-'}
                                                    </span>
                                                </td>

                                                {/* Last message preview */}
                                                <td className="px-6 py-0 align-middle max-w-[300px]">
                                                    {row.lastMessageText ? (
                                                        <div className="truncate text-gray-500">
                                                            <span className="text-sm text-gray-800 font-medium">
                                                                {messagePreviewText(
                                                                    row.lastMessageText
                                                                )}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>

                                                {/* Date — H6 fix: call timeAgo directly */}
                                                <td className="px-6 py-0 align-middle whitespace-nowrap">
                                                    {timeAgo(row.lastMessageDate)}
                                                </td>

                                                {/* Actions */}
                                                <td className="px-6 py-0 align-middle">
                                                    {unreadPatientIds.has(row.patientId) ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) =>
                                                                handleMarkAsSeen(e, row.patientId)
                                                            }
                                                            disabled={
                                                                markingSeenId === row.patientId
                                                            }
                                                            className="flex items-center justify-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg w-[120px] py-1.5 hover:bg-green-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                                                        >
                                                            {markingSeenId === row.patientId ? (
                                                                <div className="h-3.5 w-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <svg
                                                                        className="w-3.5 h-3.5"
                                                                        viewBox="0 0 14 14"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M5.25 6.41732L7 8.16732L12.8333 2.33398"
                                                                            stroke="currentColor"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                    Mark as read
                                                                </>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) =>
                                                                handleMarkAsUnread(e, row.patientId)
                                                            }
                                                            disabled={
                                                                markingUnreadId === row.patientId
                                                            }
                                                            className="flex items-center justify-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg w-[120px] py-1.5 hover:bg-orange-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                                                        >
                                                            {markingUnreadId === row.patientId ? (
                                                                <div className="h-3 w-3 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                                                            ) : (
                                                                'Mark as unread'
                                                            )}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer controls */}
                        {sortedConversations.length > 0 && (
                            <div className="flex-shrink-0 flex items-center justify-between px-6 h-14 border-t border-gray-200 rounded-b-2xl bg-white">
                                {viewMode === 'paged' ? (
                                    <div className="flex items-center">
                                        <button
                                            className="text-gray-400 text-xl px-2 py-1 rounded hover:bg-gray-100 focus:outline-none"
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            aria-label="Previous page"
                                        >
                                            &laquo;
                                        </button>
                                        <span className="mx-2">
                                            <span className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 rounded-lg bg-white text-gray-700 font-medium text-base select-none">
                                                {page}
                                            </span>
                                            <span className="mx-2 text-gray-400 text-base font-medium">
                                                of
                                            </span>
                                            <span className="text-gray-700 text-base font-medium">
                                                {totalPages}
                                            </span>
                                            {hasActiveFilters && (
                                                <span className="ml-2 text-gray-500 text-sm">
                                                    ({sortedConversations.length} result
                                                    {sortedConversations.length === 1 ? '' : 's'})
                                                </span>
                                            )}
                                        </span>
                                        <button
                                            className="text-gray-400 text-xl px-2 py-1 rounded hover:bg-gray-100 focus:outline-none"
                                            onClick={() =>
                                                setPage((p) => Math.min(totalPages, p + 1))
                                            }
                                            disabled={page === totalPages}
                                            aria-label="Next page"
                                        >
                                            &raquo;
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-sm text-gray-500">
                                        {sortedConversations.length} unread conversation
                                        {sortedConversations.length === 1 ? '' : 's'}
                                    </span>
                                )}
                                <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                                    <button
                                        className={`p-1.5 rounded-md transition-colors ${viewMode === 'paged' ? 'bg-white shadow-sm text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
                                        onClick={() => handleViewModeChange('paged')}
                                        aria-label="Paginated view"
                                        title="Paginated view"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            className="w-4 h-4"
                                        >
                                            <rect x="2" y="2" width="12" height="5" rx="1" />
                                            <rect x="2" y="9" width="12" height="5" rx="1" />
                                        </svg>
                                    </button>
                                    <button
                                        className={`p-1.5 rounded-md transition-colors ${viewMode === 'scroll' ? 'bg-white shadow-sm text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
                                        onClick={() => handleViewModeChange('scroll')}
                                        aria-label="Scrollable view"
                                        title="Scrollable view"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            className="w-4 h-4"
                                        >
                                            <rect x="2" y="1" width="12" height="14" rx="1" />
                                            <line x1="5" y1="4.5" x2="11" y2="4.5" />
                                            <line x1="5" y1="7.5" x2="11" y2="7.5" />
                                            <line x1="5" y1="10.5" x2="9" y2="10.5" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Slide-over Panel */}
            {selectedPatient && (
                <ConversationPanel
                    patient={{
                        id: selectedPatient.patientId,
                        name: selectedPatient.name,
                        treatmentStatus: selectedPatient.treatmentStatus,
                        startDate: selectedPatient.startDate,
                        alignerNumber: selectedPatient.alignerNumber,
                        alignerTotal: selectedPatient.alignerTotal,
                        requiredAction: selectedPatient.requiredAction
                    }}
                    onClose={handleClosePanel}
                />
            )}
        </div>
    );
};

export default Messages;
